import feeService from 'ApiService/Accountant/feeService';
import courseService from 'ApiService/Admin/courseService';
import batchService from 'ApiService/Admin/batchService';
import studentService from 'ApiService/Admin/studentService';

import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ThreeDots } from 'react-loader-spinner';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

import Nodatafound from 'pages/Common/Nodatafound';
import SelectClass from './Steps/SelectClass';
import AllStudents from './Steps/AllStudents';
import StudentDetails from '../StudentDetails/Index';

const Index = () => {
  /* ================= STEP ================= */
  const [currentStep, setCurrentStep] = useState(1);

  /* ================= CLASS DATA ================= */
  const [classData, setClassData] = useState({
    courseId: '',
    batchStructureId: '',
    batchId: '',
  });

  /* ================= DATA ================= */
  const [courses, setCourses] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [students, setStudents] = useState([]);

  /* ================= UI ================= */
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [disable, setDisable] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingStudent, setLoadingStudent] = useState(false);

  /* ================= UNIVERSAL SEARCH ================= */
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [studentDetailsLoading, setStudentDetailsLoading] = useState(false);

  /* ================= AUTH ================= */
  const { state, loading } = useContext(AuthContext);

  const user = state?.user;
  const role = user?.role;
  const isSuperAdmin = role === 'superadmin';

  const selectedBranch = isSuperAdmin
    ? state?.selectedBranch
    : user?.branch?._id;

  /* ================= RESET STEP ONE ================= */
  const resetStepOne = () => {
    setClassData({
      courseId: '',
      batchStructureId: '',
      batchId: '',
    });
    setBatchStructures([]);
    setStudents([]);
    setSelectedStudent(null);
    setDisable(true);
  };

  /* ================= RESET ONLY WHEN SUPERADMIN CHANGES BRANCH ================= */
  useEffect(() => {
    if (!isSuperAdmin) return;
    if (!selectedBranch) return;

    resetStepOne();
  }, [isSuperAdmin, selectedBranch]);

  /* ================= FETCH COURSES ================= */
  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      if (state.user.role === 'account') {
        const res = await feeService.getAllCourses(selectedBranch);
        setCourses(res?.data?.courses || []);
      } else {
        const res = await courseService.getAllCourses(selectedBranch);
        setCourses(res?.data?.courses || []);
      }
    } catch {
      toast.error('Failed to fetch courses');
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  /* ================= FETCH COURSES (ADMIN + SUPERADMIN SAFE) ================= */
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    if (isSuperAdmin && !selectedBranch) return;

    fetchCourses();
  }, [loading, user, isSuperAdmin, selectedBranch]);

  /* ================= FETCH STRUCTURES ================= */
  const fetchBatchStructures = async (courseId) => {
    try {
      if (state.user.role === 'account') {
        const res = await feeService.getBatchStructuresByCourse(
          courseId,
          selectedBranch
        );
        setBatchStructures(res?.data?.batchStructures || []);
      } else {
        const res = await batchService.getBatchStructuresByCourse(
          courseId,
          selectedBranch
        );
        setBatchStructures(res?.data?.batchStructures || []);
      }
    } catch {
      toast.error('Failed to fetch batch structures');
      setBatchStructures([]);
    }
  };

  /* ================= FETCH BATCHES ================= */
  const fetchBatchesByStructure = async (structureId) => {
    try {
      if (state.user.role === 'account') {
        const res = await feeService.getBatchesByBatchStructure(
          structureId,
          selectedBranch
        );
        return res?.data?.batches || [];
      } else {
        const res = await batchService.getBatchesByBatchStructure(
          structureId,
          selectedBranch
        );
        return res?.data?.batches || [];
      }
    } catch {
      toast.error('Failed to fetch batches');
      return [];
    }
  };

  /* ================= FETCH STUDENTS ================= */
  const fetchStudents = async () => {
    try {
      setLoadingStudent(true);
      const res = await feeService.getStudentDetails(classData, selectedBranch);
      setStudents(res?.data?.data || []);
    } catch {
      toast.error('Failed to fetch students');
    } finally {
      setLoadingStudent(false);
    }
  };

  /* ================= STEP HANDLERS ================= */
  const handleNext = async () => {
    if (currentStep === 1) {
      await fetchStudents();
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    resetStepOne();
    setCurrentStep(1);
  };

  const clearData = () => {
    setStudents([]);
  };

  /* ================= UNIVERSAL SEARCH HANDLERS ================= */
  const handleSearchStudents = async () => {
    const q = searchQuery.trim();
    if (!q || q.length < 2) {
      toast.error('Enter at least 2 characters to search');
      return;
    }
    setSearchLoading(true);
    setShowSearchResults(true);
    try {
      const res = await studentService.searchStudents(q, selectedBranch);
      setSearchResults(res.data.data || []);
    } catch {
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') handleSearchStudents();
  };

  const handleStudentClick = async (student) => {
    setStudentDetailsLoading(true);
    try {
      const res = await studentService.getStudentDetailsById(student._id);
      if (res.data.success) {
        setSelectedStudent(res.data.data);
        setShowStudentDetails(true);
      } else {
        toast.error('Failed to fetch student details');
      }
    } catch {
      toast.error('Error fetching student details');
    } finally {
      setStudentDetailsLoading(false);
    }
  };

  const handleBackFromDetails = () => {
    setSelectedStudent(null);
    setShowStudentDetails(false);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  /* ================= STEP RENDER ================= */
  const displayStep = (step) => {
    switch (step) {
      case 1:
        return showStudentDetails ? (
          <StudentDetails
            selectedStudent={selectedStudent}
            handleBackClick={handleBackFromDetails}
            studentDetailsLoading={studentDetailsLoading}
            selectedBranch={selectedBranch}
            fetchStudents={() => {}}
          />
        ) : (
          <div>
            {/* ================= UNIVERSAL SEARCH SECTION ================= */}
            <div className="mb-6 rounded-lg border border-brand-200 bg-brand-50 p-4 dark:border-navy-600 dark:bg-navy-700">
              <h4 className="mb-3 text-center text-lg font-bold">
                Universal Student Search
              </h4>
              <p className="mb-3 text-center text-sm text-gray-500">
                Search across all students by Name, Email, or Mobile Number
              </p>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Search by Name, Email, or Mobile Number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="block w-full rounded-xl border border-gray-300 bg-white p-3 text-sm outline-none focus:border-brand-500 dark:border-navy-500 dark:bg-navy-800 dark:text-white"
                />
                <button
                  onClick={handleSearchStudents}
                  disabled={searchLoading}
                  className="linear cursor-pointer rounded-xl bg-brand-900 px-6 py-3 font-medium text-white transition duration-200 hover:bg-brand-800 disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
                >
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
                {showSearchResults && (
                  <button
                    onClick={clearSearch}
                    className="linear cursor-pointer rounded-xl bg-gray-200 px-4 py-3 font-medium text-gray-700 transition duration-200 hover:bg-gray-300 dark:bg-navy-600 dark:text-white dark:hover:bg-navy-500"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* SEARCH RESULTS */}
              {showSearchResults && (
                <div className="mt-4">
                  {searchLoading ? (
                    <div className="flex justify-center py-8">
                      <ThreeDots color="#5431ed" />
                    </div>
                  ) : searchResults.length === 0 ? (
                    <Nodatafound title="No students found matching your search" />
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr>
                            <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                              Sr No
                            </th>
                            <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                              Name
                            </th>
                            <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                              Email
                            </th>
                            <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                              Contact Number
                            </th>
                            <th className="border-b border-gray-200 pb-2 pl-4 pr-4 text-center text-sm font-bold tracking-wide text-gray-600 dark:text-white">
                              Gender
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchResults.map((student, index) => (
                            <tr
                              key={student._id}
                              onClick={() => handleStudentClick(student)}
                              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-navy-600"
                            >
                              <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-gray-700 dark:text-white">
                                {index + 1}
                              </td>
                              <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm font-bold text-gray-700 dark:text-white">
                                {student.name}
                              </td>
                              <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm text-gray-700 dark:text-white">
                                {student.email}
                              </td>
                              <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm text-gray-700 dark:text-white">
                                {student.contactNumber}
                              </td>
                              <td className="pb-2 pl-4 pr-4 pt-2 text-center text-sm text-gray-700 dark:text-white">
                                {student.gender}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="mt-2 text-center text-xs text-gray-400">
                        Showing up to 50 results. Click a row to view details.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ================= EXISTING BATCH SELECTOR ================= */}
            <h4 className="mb-2 text-center text-lg font-bold">
              OR Select Class Information
            </h4>
            <SelectClass
              setDisable={setDisable}
              classData={classData}
              setClassData={setClassData}
              courses={courses}
              loadingCourses={loadingCourses}
              batchStructures={batchStructures}
              fetchBatchStructures={fetchBatchStructures}
              fetchBatchesByStructure={fetchBatchesByStructure}
              selectedBranch={selectedBranch}
              clearData={clearData}
            />
          </div>
        );

      case 2:
        return (
          <AllStudents
            students={students}
            selectedStudent={selectedStudent}
            setSelectedStudent={setSelectedStudent}
            classData={classData}
            loadingStudent={loadingStudent}
          />
        );

      default:
        return null;
    }
  };

  /* ================= LOADERS ================= */
  if (loading) {
    return (
      <div className="flex h-60 items-center justify-center">
        Loading user...
      </div>
    );
  }

  if (isSuperAdmin && !selectedBranch) {
    return (
      <div className="flex h-60 items-center justify-center">
        Please select a branch
      </div>
    );
  }

  return (
    <div className="mt-3 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600 md:h-[390px]" />

      <div className="mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
        <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[70px] pb-3">
          <UseContextProvider>{displayStep(currentStep)}</UseContextProvider>

          <div className="flex justify-end px-3 pt-4">
            {currentStep === 2 && (
              <button
                onClick={handleBack}
                className="mr-3 rounded-xl bg-gray-200 px-14 py-2"
              >
                Back
              </button>
            )}

            {currentStep === 1 && (
              <button
                disabled={disable}
                onClick={handleNext}
                className="rounded-xl bg-brand-900 px-16 py-2 text-white disabled:opacity-60"
              >
                Next
              </button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Index;
